<template>
  <div class="player">
    <OSDHeader
      :is-show-osd="isOSDActive"
      :is-back-active="getIsBackButtonActive"
      @hide="() => (isOSDActive = false)"
    />
    <ViewerImage v-if="isImage" :src="src" />
    <VideoPlayer
      v-if="isVideo"
      :src="src"
      :seek-to="seekTo"
      :title="title"
      :description="description"
      :value="currentPosition"
      :status="videoStatus"
      :autoplay="autoplay"
      :loop="loop"
      :controls="controls"
      @status="handleVideoStatus"
      @input="updatePlayPosition"
      @setVideo="assignVideoObject"
    />
    <WebViewer v-if="isWebsite" :src="src" :scroll-position="scrollPosition" />
    <div v-else>
      <table>
        <tr>
          <th>Key</th>
          <th>Value</th>
          <th>Allowed values</th>
        </tr>

        <tr>
          <td>type</td>
          <td>{{ type }}</td>
          <td>VIDEO, IMAGE, WEBSITE</td>
        </tr>
        <tr>
          <td>src</td>
          <td>{{ src }}</td>
        </tr>
        <tr>
          <td>title</td>
          <td>{{ title }}</td>
        </tr>
        <tr>
          <td>description</td>
          <td>{{ description }}</td>
        </tr>
        <tr>
          <td>seekTo</td>
          <td>{{ seekTo }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import ViewerImage from '@/components/ViewerImage.vue';
import VideoPlayer, { VideoStates } from '@/components/VideoPlayer';
import WebViewer from '@/components/WebViewer.vue';
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import router from '@/router';
import { MEDIA_TYPE } from '@/helpers/MediaHandler';
import OSDHeader from '@/components/OSDHeader.vue';
import { NAV_TYPE } from '@/store/modules/navigation';

export default {
  mixins: [keyHandler],
  components: {
    ViewerImage,
    VideoPlayer,
    WebViewer,
    OSDHeader,
  },
  props: {
    type: String,
    src: String,
    seekTo: Number,
    title: String,
    description: String,
    autoplay: Boolean,
    loop: Boolean,
    controls: {
      type: Boolean,
      default: true,
    },
  },
  emits: {},
  data() {
    return {
      currentPosition: 0,
      videoStatus: null,
      scrollPosition: 0,
      disableBackButton: false,
      isButtonBackActive: false,
      isOSDActive: true,
      video: undefined,
    };
  },
  mounted() {
    if (this.isVideo) {
      this.videoStatus = VideoStates.PLAY;
    }
    if (this.isWebsite) {
      window.addEventListener('message', this.handleIncomingMessage);
    }
  },
  beforeDestroy() {
    this.disableBackButton = false;
    if (this.isWebsite) {
      window.removeEventListener('message', this.handleIncomingMessage);
    }
  },
  methods: {
    assignVideoObject(video) {
      this.video = video;
    },
    handleIncomingMessage(e) {
      if (e.message === 'disable-back-button') {
        this.disableBackButton = true;
      }
    },
    updatePlayPosition(value) {
      this.currentPosition = value;
    },
    handleVideoStatus(status) {
      if (status !== this.videoStatus) {
        this.videoStatus = status;
      }
    },
    eventHandler(key, event) {
      this.isOSDActive = true;
      this.$eventBus.$emit('key-action', { key, event });
      if (this.isVideo) {
        this.videoKeyHandler(key);
      }
      if (this.isWebsite) {
        this.webKeyHandler(key, event);
      }
      if (this.isImage) {
        this.imageKeyHandler(key, event);
      }
    },
    videoKeyHandler(key) {
      switch (key) {
        case KEYS.ENTER:
          if (this.getIsBackButtonActive) {
            this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          }
          if (!this.videoStatus || this.videoStatus === VideoStates.PAUSE) {
            this.videoStatus = VideoStates.PLAY;
            break;
          }
          this.videoStatus = VideoStates.PAUSE;
          break;
        case KEYS.RIGHT:
          if (!this.video) break;
          this.video.currentTime = this.video.currentTime + 15;
          break;
        case KEYS.LEFT:
          if (!this.video) break;
          this.video.currentTime = this.video.currentTime - 15;
          if (this.video.currentTime < 0) {
            this.video.currentTime = 0;
          }
          break;
        case KEYS.UP:
          this.isButtonBackActive = true;
          break;
        case KEYS.DOWN:
          this.isButtonBackActive = false;
          break;
      }
      this.handleGlobalKeys(key);
    },
    webKeyHandler(key, event) {
      this.$eventBus.$emit('key-handler', { key, event });
      switch (key) {
        case KEYS.ENTER:
          if (this.getIsBackButtonActive) {
            this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          }
          break;
        case KEYS.UP:
          this.isButtonBackActive = true;
          break;
        case KEYS.DOWN:
          this.isButtonBackActive = false;
          break;
      }
      if (!this.disableBackButton) this.handleGlobalKeys(key);
    },
    imageKeyHandler(key, event) {
      switch (key) {
        case KEYS.BACK:
        case KEYS.ENTER:
          this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
      }
    },
    handleGlobalKeys(key, event) {
      switch (key) {
        case KEYS.BACK:
          this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
      }
    },
  },
  computed: {
    getId() {
      return this.$route.params.id;
    },
    isImage() {
      return this.type === MEDIA_TYPE.IMAGE;
    },
    isVideo() {
      return this.type === MEDIA_TYPE.VIDEO;
    },
    isWebsite() {
      return this.type === MEDIA_TYPE.WEBSITE;
    },
    getIsBackButtonActive() {
      if (this.isImage) return true;
      if (!this.controls) return true;
      return this.isButtonBackActive;
    },
  },
};
</script>

<style scoped>
.player--info {
  padding: 30px;
  text-align: left;
  margin: 0 auto;
  width: 50%;
}
.player--info td,
.player--info th {
  padding: 0 1rem;
}
</style>



// WEBPACK FOOTER //
// src/pages/MediaPlayer.vue